import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../AuthProvider';
import { useNavigate } from 'react-router-dom';
import AdminNav from './AdminNav';

const AdminPage = () => {
  const { loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [constants, setConstants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [checkingRole, setCheckingRole] = useState(true);

  // Edit constant state
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const checkAdminRole = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          navigate('/login');
          return;
        }

        // Check for admin or PM role in the Supabase session JWT metadata
        const userRole = session.user.app_metadata?.role || session.user.role;
        console.log('DEBUG - User Session Info:', {
          app_metadata: session.user.app_metadata,
          role_field: session.user.role,
          detected_role: userRole
        });
        
        if (userRole === 'admin' || userRole === 'pm') {
          setIsAdmin(true);
          fetchConstants();
        } else {
          setIsAdmin(false);
          setError('Access denied. Only admin and PM can access this page.');
        }
      } catch (err) {
        console.error('Error checking admin role:', err);
        setError('Failed to verify permissions.');
      } finally {
        setCheckingRole(false);
      }
    };

    if (!authLoading) {
      checkAdminRole();
    }
  }, [authLoading, navigate]);

  const fetchConstants = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('admin_constants')
        .select('*')
        .order('key');

      if (error) throw error;
      
      // Filter out evaluation prompts (they're on the Synopsis Prompt page)
      const filteredData = (data || []).filter(c => 
        !c.key.startsWith('OFFICIAL_EVALUATION_') && c.key !== 'COMPARISON_EVALUATION_PROMPT'
      );
      
      // Custom sorting logic
      const sortedData = filteredData.sort((a, b) => {
        return a.key.localeCompare(b.key);
      });

      setConstants(sortedData);
    } catch (err) {
      console.error('Error fetching constants:', err);
      setError('Failed to load constants.');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateConstant = async (id) => {
    try {
      setIsSaving(true);
      const { error } = await supabase
        .from('admin_constants')
        .update({ value: editValue, updated_at: new Date().toISOString() })
        .eq('id', id);

      if (error) throw error;
      
      setEditingId(null);
      setEditValue('');
      fetchConstants();
    } catch (err) {
      console.error('Error updating constant:', err);
      alert(`Error: ${err.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  const startEditing = (constant) => {
    setEditingId(constant.id);
    setEditValue(constant.value);
  };

  if (authLoading || checkingRole) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-foreground text-xl font-semibold">Checking permissions...</div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-background p-4 text-center">
        <h1 className="text-destructive text-2xl font-bold mb-4">Access Denied</h1>
        <p className="text-muted-foreground mb-6">{error || 'Only admin and PM can view this page.'}</p>
        <button 
          onClick={() => navigate('/')}
          className="bg-primary text-primary-foreground hover:opacity-90 px-6 py-2 rounded-lg font-medium transition-opacity"
        >
          Return to Home
        </button>
      </div>
    );
  }

  return (
    <div className="h-full w-full overflow-y-auto bg-gray-100 p-8">
      <div className="max-w-6xl mx-auto pb-12">
        <div className="flex justify-between items-center mb-8">
          <div className="flex flex-col">
            <h1 className="text-foreground text-3xl font-medium">Admin Panel</h1>
            <p className="text-muted-foreground text-sm mt-2">
              This panel is for admin and PM users. User Management, Synopsis Prompt, and Keyword Prompt are available here.
            </p>
          </div>
          <button 
            onClick={() => navigate('/')}
            className="text-muted-foreground hover:text-foreground text-sm font-medium whitespace-nowrap border border-border rounded-lg px-3 py-1.5 hover:bg-muted transition-colors"
          >
            ← Back to App
          </button>
        </div>

        <AdminNav />

        {/* Constants List */}
        <div className="space-y-8">
          <div className="flex justify-between items-center">
            <h2 className="text-foreground text-xl font-semibold"> </h2>
            <button 
              onClick={fetchConstants}
              className="text-primary hover:opacity-90 text-sm font-medium transition-opacity"
            >
              Refresh
            </button>
          </div>
          
          {loading ? (
            <div className="bg-card rounded-lg shadow-subtle-sm p-8 text-center text-muted-foreground border border-border">
              Loading constants...
            </div>
          ) : constants.length === 0 ? (
            <div className="bg-card rounded-lg shadow-subtle-sm p-8 text-center text-muted-foreground border border-border">
              No constants found.
            </div>
          ) : (
            <>
              <div className="space-y-4">
                {constants.map((c) => (
                    <div key={c.id} className="bg-card rounded-lg shadow-subtle-sm border border-border overflow-hidden">
                      <div className="px-6 py-4 border-b border-border bg-muted/30 flex justify-between items-center">
                        <span className="font-mono font-bold text-card-foreground">{c.key}</span>
                        <div className="flex space-x-3">
                          {editingId !== c.id && (
                            <button
                              onClick={() => startEditing(c)}
                              className="text-primary hover:opacity-90 text-sm font-medium transition-opacity"
                            >
                              Edit
                            </button>
                          )}
                        </div>
                      </div>
                      
                      <div className="p-6 space-y-4">
                        <div>
                          <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Value</label>
                          {editingId === c.id ? (
                            <div className="space-y-3">
                              <textarea
                                value={editValue}
                                onChange={(e) => setEditValue(e.target.value)}
                                className="w-full px-3 py-2 border border-border rounded-lg bg-input text-foreground focus:ring-2 focus:ring-ring focus:outline-none min-h-[200px] font-mono text-sm"
                              />
                              <div className="flex space-x-2">
                                <button
                                  onClick={() => handleUpdateConstant(c.id)}
                                  disabled={isSaving}
                                  className="bg-primary text-primary-foreground hover:opacity-90 px-4 py-2 rounded-lg text-sm font-medium transition-opacity disabled:opacity-70"
                                >
                                  {isSaving ? 'Saving...' : 'Save Changes'}
                                </button>
                                <button
                                  onClick={() => setEditingId(null)}
                                  className="bg-muted text-muted-foreground hover:bg-muted/80 px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className="whitespace-pre-wrap font-mono text-sm bg-muted/30 p-4 rounded-lg border border-border max-h-[400px] overflow-y-auto text-foreground leading-relaxed">
                              {c.value}
                            </div>
                          )}
                        </div>
                        
                        {c.description && (
                          <div>
                            <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">Description</label>
                            <p className="text-muted-foreground text-sm">{c.description}</p>
                          </div>
                        )}
                        
                        <div className="pt-2 text-muted-foreground text-[10px] flex justify-between italic">
                          <span>ID: {c.id}</span>
                          <span>Last Updated: {new Date(c.updated_at).toLocaleString()}</span>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            </>
          )}
        </div>
        
        <div className="mt-8 text-center text-muted-foreground text-sm">
          <p>Note: This is a temporary admin test page. Access is restricted via Supabase RLS.</p>
        </div>
      </div>
    </div>
  );
};

export default AdminPage;
